import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { TbDeviceFloppy, TbCheck, TbAlertTriangle } from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import DropdownSelect from '../../components/forms/DropdownSelect';
import Labeled from '../../components/misc/Labeled';
import {
  BookYear,
  Budget,
  FinancialCategory,
  UpsertBudgetDto,
  UpsertBudgetLineDto,
} from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';

interface BudgetSettingsPageProps {
  hideHeader?: boolean;
}

interface DraftLine extends UpsertBudgetLineDto {
  key: string; // stable key per row (category id or generated)
}

export default function BudgetSettingsPage({
  hideHeader = false,
}: BudgetSettingsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.budget' });
  const locale = i18n.language;

  const [bookYears, setBookYears] = useState<BookYear[]>([]);
  const [selectedBookYearId, setSelectedBookYearId] = useState<number | null>(null);
  const [categories, setCategories] = useState<FinancialCategory[]>([]);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const refreshBookYears = useCallback(async () => {
    const data = await bankAccountService.getBookYears();
    setBookYears(data);
    if (data.length > 0 && selectedBookYearId === null) {
      const today = new Date();
      const current =
        data.find((by) => dayjs(by.startDate).isBefore(today) && dayjs(by.endDate).isAfter(today)) ??
        data[0];
      setSelectedBookYearId(current.bookYearId);
    }
  }, [selectedBookYearId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await refreshBookYears();
      } catch (error: unknown) {
        toast.error((error as Error)?.message ?? String(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshBookYears]);

  // Load categories once
  useEffect(() => {
    bankAccountService
      .getCategories()
      .then(setCategories)
      .catch((error: unknown) =>
        toast.error((error as Error)?.message ?? String(error)),
      );
  }, []);

  // Load budget whenever selected book year changes
  useEffect(() => {
    if (selectedBookYearId === null) return;
    setLoading(true);
    bankAccountService
      .getBudget(selectedBookYearId)
      .then((b) => {
        setBudget(b);
        setNotes(b?.notes ?? '');
        const linesByCategory = new Map<number, { income: number; expense: number }>();
        (b?.lines ?? []).forEach((line) => {
          linesByCategory.set(line.financialCategoryId, {
            income: line.budgetedIncome,
            expense: line.budgetedExpense,
          });
        });
        setDraft(
          categories.map((c) => {
            const existing = linesByCategory.get(c.financialCategoryId);
            return {
              key: `cat-${c.financialCategoryId}`,
              financialCategoryId: c.financialCategoryId,
              budgetedIncome: existing?.income ?? 0,
              budgetedExpense: existing?.expense ?? 0,
            };
          }),
        );
        setDirty(false);
      })
      .catch((error: unknown) =>
        toast.error((error as Error)?.message ?? String(error)),
      )
      .finally(() => setLoading(false));
  }, [selectedBookYearId, categories]);

  const updateLine = (
    financialCategoryId: number,
    field: 'budgetedIncome' | 'budgetedExpense',
    rawValue: string,
  ) => {
    const parsed = Math.round(parseFloat(rawValue.replace(',', '.')) * 100);
    const safe = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    setDraft((prev) =>
      prev.map((line) =>
        line.financialCategoryId === financialCategoryId ? { ...line, [field]: safe } : line,
      ),
    );
    setDirty(true);
  };

  const totals = useMemo(() => {
    return draft.reduce(
      (acc, line) => ({
        income: acc.income + (line.budgetedIncome ?? 0),
        expense: acc.expense + (line.budgetedExpense ?? 0),
      }),
      { income: 0, expense: 0 },
    );
  }, [draft]);

  const handleSave = async () => {
    if (selectedBookYearId === null) return;
    setSaving(true);
    try {
      const dto: UpsertBudgetDto = {
        notes: notes.trim() ? notes.trim() : null,
        lines: draft
          .filter((line) => line.budgetedIncome > 0 || line.budgetedExpense > 0)
          .map(({ financialCategoryId, budgetedIncome, budgetedExpense }) => ({
            financialCategoryId,
            budgetedIncome,
            budgetedExpense,
          })),
      };
      const saved = await bankAccountService.upsertBudget(selectedBookYearId, dto);
      setBudget(saved);
      setDirty(false);
      toast.success(t('success.saved'));
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
        (error as Error)?.message ??
        String(error);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const bookYearOptions = useMemo(
    () =>
      bookYears.map((by) => ({
        label: by.label ?? `Boekjaar ${dayjs(by.startDate).year()}`,
        value: by.bookYearId,
      })),
    [bookYears],
  );

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button
              variant="primary-dark"
              onClick={handleSave}
              disabled={saving || loading || !dirty}>
              <TbDeviceFloppy />
              {saving ? t('actions.saving') : t('actions.save')}
            </Button>
          }
        />
      )}

      {hideHeader && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="primary-dark"
            onClick={handleSave}
            disabled={saving || loading || !dirty}>
            <TbDeviceFloppy />
            {saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </div>
      )}

      <div className="space-y-6">
        <div className="max-w-md">
          <Labeled label={t('bookYearLabel')}>
            <DropdownSelect
              options={bookYearOptions}
              value={selectedBookYearId ?? undefined}
              setValue={(v) => setSelectedBookYearId(typeof v === 'number' ? v : null)}
            />
          </Labeled>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : draft.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            {t('emptyState')}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    {t('table.category')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                    {t('table.income')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                    {t('table.expense')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {draft.map((line) => {
                  const category = categories.find(
                    (c) => c.financialCategoryId === line.financialCategoryId,
                  );
                  return (
                    <tr key={line.key}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: category?.color ?? '#6B7280' }}
                          />
                          <span>{category?.name ?? `Category #${line.financialCategoryId}`}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          className="w-32 rounded-md border border-gray-300 px-2 py-1 text-right text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={(line.budgetedIncome / 100).toFixed(2)}
                          onChange={(e) => updateLine(line.financialCategoryId, 'budgetedIncome', e.target.value)}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          className="w-32 rounded-md border border-gray-300 px-2 py-1 text-right text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={(line.budgetedExpense / 100).toFixed(2)}
                          onChange={(e) => updateLine(line.financialCategoryId, 'budgetedExpense', e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{t('table.total')}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-success">
                    {formatEuroCentsWithSign(totals.income, locale, false)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-danger">
                    {formatEuroCentsWithSign(totals.expense, locale, false)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div>
          <Labeled label={t('notesLabel')}>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setDirty(true);
              }}
              placeholder={t('notesPlaceholder')}
            />
          </Labeled>
        </div>

        {dirty && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <TbAlertTriangle />
            {t('unsavedChanges')}
          </div>
        )}

        {!dirty && budget && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <TbCheck />
            {t('savedAt', { date: dayjs(budget.updatedAt).format('DD-MM-YYYY HH:mm') })}
          </div>
        )}
      </div>
    </>
  );
}